<template>
  <button @click="savePdf">
    <slot>Save To PDF</slot>
    <div v-if="hasInactivePDF" style="font-size: 0.8em; padding-top: 0.3em">
      Warning. Outliers cannot be exported to PDF yet.
    </div>
  </button>
</template>

<script>
// Models
import PigeonDocModel from '../../models/PigeonDocModel';

// PDF make
import pdfMake from 'pdfmake';
import pdfFonts from './vfs_fonts.js';
import standardPageSizes from 'pdfmake/src/standardPageSizes';

const DEFAULT_STYLES = {
  title: {
    fontSize: 26,
    bold: true,
    margin: [0, 22],
  },
  'title-heading': {
    margin: [0, 270, 0, 30],
    fontSize: 45,
    bold: true,
    alignment: 'center',
  },
  'sub-title': {
    margin: [0, 0, 0, 30],
    fontSize: 30,
    bold: true,
    alignment: 'center',
  },
  'heading-h1': {
    fontSize: 20,
    bold: true,
    margin: [0, 20],
  },
  'heading-h2': {
    fontSize: 18,
    bold: true,
    margin: [0, 20],
  },
  'heading-h3': {
    fontSize: 17,
    bold: true,
    margin: [0, 20],
  },
  strong: {
    bold: true,
  },
  i: {
    italics: true,
  },
  b: {
    bold: true,
  },
  defaultTable: {
    margin: [0, 20],
    lineHeight: 1.25,
  },
};

export default {
  name: 'SaveToPdf',
  props: {
    document: {
      type: Object,
      default: new PigeonDocModel(),
    },
  },
  methods: {
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString('en-GB');
    },
    getBase64ImageFromURL(url) {
      return new Promise((resolve, reject) => {
        var img = new Image();
        img.setAttribute('crossOrigin', 'anonymous');

        img.onload = () => {
          var canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;

          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          var dataURL = canvas.toDataURL('image/png');

          resolve(dataURL);
        };

        img.onerror = (error) => {
          reject(error);
        };

        let imgloc = url;
        /**
         * backwards compatability, strip window location from old image urls.
         * previous version saved the whole url newer versions save only relative img path
         */
        let re = new RegExp('^(http|https)://', 'i');
        if (re.test(url)) {
          let regex = new RegExp('(\.(io|com|ca)\/)|:8080', 'g');
          regex.exec(url);
          imgloc = url.slice(regex.lastIndex);
        }

        img.src = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}${imgloc}`;
      });
    },
    async convertImagesToBase64(images) {
      let allImages = {};
      await Promise.all(
        Object.keys(images).map(async (imageName) => {
          allImages[imageName] = await this.getBase64ImageFromURL(images[imageName]);
        }),
      );
      return allImages;
    },
    async convertNodesToPDFMake(docDefinition) {
      let content = [];

      for (let i = 0; i < this.document.nodes.length; i++) {
        if (this.document.nodes[i].type == 'titlePage') {
          let title = {
            text: this.document.nodes[i].content.heading,
            style: 'title-heading',
          };
          content.push(title);
          if (this.document.nodes[i].content.subheading) {
            let subTitle = {
              text: this.document.nodes[i].content.subheading,
              style: 'sub-title',
            };
            content.push(subTitle);
          }
          if (this.document.nodes[i].content.date) {
            let date = {
              text: this.formatDate(this.document.nodes[i].content.date),
              style: 'sub-title',
            };
            content.push(date);
          }
          // add a page break to the last title component
          for (let x = 0; x < content.length; x++) {
            if (x == content.length - 1) {
              content[x]['pageBreak'] = 'after';
            }
          }
        } else if (this.document.nodes[i].type === 'section') {
          for (let x = 0; x < this.document.nodes[i].content.length; x++) {
            let addPageBreak = false;
            if (
              this.document.nodes[i].content[x].type === 'heading' &&
              this.document.nodes[i].content[x].id.includes('q-group-heading-')
            ) {
              addPageBreak = true;
            }
            const cleanedSubNode = await this.checkNodeByType(
              this.document.nodes[i].content[x],
              docDefinition,
            );
            if (cleanedSubNode) {
              if (i === 0) {
                cleanedSubNode.pageBreak = '';
              }
              if (addPageBreak) {
                cleanedSubNode.pageBreak = 'before';
              }
              if (
                this.document.nodes[i].content[x].type === 'horizontalBarChart' ||
                this.document.nodes[i].type === 'horizontalLineChart'
              ) {
                cleanedSubNode.style = 'heading-h2';
              }
              content.push(cleanedSubNode);
            }
          }
        } else if (this.document.nodes[i].type === 'heading') {
          const cleanedNode = await this.checkNodeByType(this.document.nodes[i], docDefinition);
          if (cleanedNode) {
            if (i === 0) {
              cleanedNode.pageBreak = '';
            }
            cleanedNode.style = 'heading-h1';
            content.push(cleanedNode);
          }
        } else if (
          this.document.nodes[i].type === 'horizontalBarChart' ||
          this.document.nodes[i].type === 'horizontalLineChart'
        ) {
          const cleanedNode = await this.checkNodeByType(this.document.nodes[i], docDefinition);
          if (cleanedNode) {
            if (i === 0) {
              cleanedNode.pageBreak = '';
            }
            cleanedNode.style = 'heading-h2';
            content.push(cleanedNode);
          }
        } else {
          const cleanedNode = await this.checkNodeByType(this.document.nodes[i], docDefinition);
          if (cleanedNode) {
            if (i === 0) {
              cleanedNode.pageBreak = '';
            }
            content.push(cleanedNode);
          }
        }
      }
      return content;
    },
    async savePdf() {
      // clone the document to avoid mutating the prop
      const docCopy = new PigeonDocModel({
        ...this.document,
        pdfConfig: {
          ...this.document.pdfConfig,
        },
      });

      // import fonts
      if (pdfMake.vfs === undefined) pdfMake.vfs = pdfFonts.pdfMake.vfs;

      pdfMake.fonts = {
        'Open Sans': {
          normal: 'OpenSans-Regular.ttf',
          bold: 'OpenSans-Bold.ttf',
          italics: 'OpenSans-Italic.ttf',
          bolditalics: 'OpenSans-BoldItalic.ttf',
        },
        'Open Sans Condensed': {
          normal: 'OpenSansCondensed-Bold.ttf',
        },
      };

      let docDefinition = {
        content: [],
        styles: DEFAULT_STYLES,
        defaultStyle: {
          fontSize: 12,
          font: 'Open Sans',
        },
        pageSize: 'A3',
      };

      if (docCopy.pdfConfig) {
        if (this.hasBanner) {
          if (docCopy.pdfConfig.images) {
            docDefinition.images = await this.convertImagesToBase64(docCopy.pdfConfig.images);
          }
        } else {
          docCopy.pdfConfig.header = {};
        }

        if (docCopy.pdfConfig.footer) {
          const customFooterText = docCopy.pdfConfig.footer;
          docDefinition.footer = function (currentPage, pageCount, pageSize) {
            const footer = [];

            footer.push({
              svg: '<svg width="850" height="1" viewBox="0 0 850 1"><line y1="0.5" x2="850" y2="0.5" stroke="black"/></svg>',
              width: pageSize.width - 50,
              margin: [0, 10, 0, 5],
              alignment: 'center',
            });

            footer.push({
              columns: [
                { text: `Page ${currentPage} of ${pageCount}`, alignment: 'left' },
                customFooterText,
              ],
              margin: [25, 5, 25, 25],
              fontSize: 9,
            });

            return footer;
          };
        }

        if (docCopy.pdfConfig.styles) {
          docDefinition.styles = Object.assign(DEFAULT_STYLES, docCopy.pdfConfig.styles);
        }

        docDefinition = Object.assign(docDefinition, docCopy.getPDFConfig());
      }

      const content = await this.convertNodesToPDFMake(docDefinition);
      docDefinition.content = content;

      const doc = pdfMake.createPdf(docDefinition);
      doc.download('report.pdf');

      // emit updated document
      this.$emit('updateDocument', docCopy);
    },

    async checkNodeByType(node, docDefinition) {
      if (node.meta && node.meta.pdfConfig && node.meta.pdfConfig.ignore) {
        return;
      }
      // TODO Add outliersUL
      if (['heading', 'text', 'table', 'textThemeTable'].includes(node.type)) {
        return await node.toPDFMake();
      } else if (
        [
          'horizontalBarChart',
          'horizontalLineChart',
          'verticalBarChart',
          'priorityMatrix',
        ].includes(node.type)
      ) {
        return await node.toPDFMake({
          ...docDefinition,
          width: standardPageSizes[docDefinition.pageSize][0] - 100,
          height: standardPageSizes[docDefinition.pageSize][1] - 200,
        });
      }
    },
  },
  computed: {
    hasInactivePDF() {
      let foundInactive = false;
      for (let i = 0; i < this.document.nodes.length; i++) {
        if (this.document.nodes[i].type === 'section') {
          for (let x = 0; x < this.document.nodes[i].content.length; x++) {
            if (this.document.nodes[i].content[x].type === 'outliersUL') {
              foundInactive = true;
            }
          }
        } else {
          if (this.document.nodes[i].type === 'outliersUL') {
            foundInactive = true;
          }
        }
      }
      return foundInactive;
    },
    hasBanner() {
      let foundBanner = false;
      if (this.document.nodes.length) {
        for (let i = 0; i < this.document.nodes.length; i++) {
          if (this.document.nodes[i].type === 'banner') {
            foundBanner = true;
            break;
          }
        }
        if (foundBanner) {
          return true;
        }
      }
      return false;
    },
  },
};
</script>
